import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type { Expression } from '../../expression'
import * as node from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'

import { And } from './and'
import { GreaterThanOrEqualTo } from './greater-than-or-equal-to'
import { Similarity } from './similarity'
import { CommonArgs } from './utils'

const DEFAULT_THRESHOLD = 0.2

/**
 * Checks if all provided expressions are similar to each other based on a similarity threshold.
 *
 * {@link DEFAULT_THRESHOLD Default} similarity threshold is used when no
 * explicitly specified
 *
 * @example
 * {"isSimilar": [A, B]}
 * // true if A is similar to B with DEFAULT_TRASH_HOLD
 *
 * {"isSimilar": [A, B, C]}
 * // true if (A is similar to B) AND (A is similar to C) AND (B is similar to C) with DEFAULT_TRASH_HOLD
 *
 * { "isSimilar": { "threshold": 0.5, "expressions": [A, B] } }
 * // true if A is similar to B with 0.5 similarity threshold
 *
 * { "isSimilar": { "threshold": 0.5, "expressions": [A, B, C] } }
 * // true if (A is similar to B) AND (A is similar to C) AND (B is similar to C) with 0.5 similarity threshold
 */
export interface IsSimilar<out T = any> {
  /** {@link IsSimilar} */
  readonly [Key.IsSimilar]: IsSimilar.Args | IsSimilar.Args.WithThreshold
}
export namespace IsSimilar {
  export const isIsSimilar: TypeChecker<IsSimilar> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.object<IsSimilar>({
        [Key.IsSimilar]: TypeChecker.Factory.union(
          Args.isArgs,
          Args.WithThreshold.isWithThreshold,
        ),
      }),
  ).usingAlias('Fn.IsSimilar')

  export const assertIsIsSimilar: TypeAsserter<IsSimilar> =
    TypeAsserter.Factory.from(isIsSimilar)

  /**
   * List of expressions to compare for similarity.
   * When no threshold is explicitly provided, {@link DEFAULT_THRESHOLD} is
   * used as the default.
   */
  export type Args = readonly Expression<any>[]
  export namespace Args {
    export const isArgs = CommonArgs.isListOfExpressions

    export interface WithThreshold {
      /**
       * Similarity threshold for string comparison (0-1 range).
       * 0 = completely different, 1 = identical strings.
       */
      threshold: number
      /** @see {@link Args} */
      expressions: Args
    }
    export namespace WithThreshold {
      export const isWithThreshold: TypeChecker<WithThreshold> =
        TypeChecker.Factory.lazy(() =>
          TypeChecker.Factory.object<WithThreshold>({
            expressions: Args.isArgs,
            threshold: TypeChecker.isNumber,
          }).usingAlias('Fn.IsSimilar.WithThreshold'),
        )
    }
  }

  export const HELPER: FnHelper<IsSimilar> = {
    createNode: (fn, context) => {
      const args = fn[Key.IsSimilar]
      // Determine the similarity threshold - use provided value or default to
      // DEFAULT_THRESHOLD
      const threshold = Args.WithThreshold.isWithThreshold(args)
        ? args.threshold
        : DEFAULT_THRESHOLD
      // Extract the expressions to compare - either from WithThreshold object
      // or directly from args
      const expressions = Args.WithThreshold.isWithThreshold(args)
        ? args.expressions
        : args

      // TODO: Potential optimization: deduplicate value nodes with the same value

      // Array to store similarity comparison nodes for each pair
      const pairwiseSimilarityNodes: GreaterThanOrEqualTo.Node<boolean>[] = []

      for (let l = 0; l < expressions.length - 1; l++) {
        for (let r = l + 1; r < expressions.length; r++) {
          const leftNode = node.from(expressions[l], context)
          const rightNode = node.from(expressions[r], context)

          // Create a similarity check: similarity(left, right) >= threshold
          pairwiseSimilarityNodes.push(
            new GreaterThanOrEqualTo.Node([
              new Similarity.Node([leftNode, rightNode]),
              new node.ForValue(threshold),
            ]),
          )
        }
      }

      // Return AND of all pairwise similarity checks:
      //     (similarity(A, B) >= threshold)
      // AND (similarity(A, C) >= threshold)
      // AND (similarity(B, C) >= threshold)
      return new And.Node(pairwiseSimilarityNodes)
    },
    expressionsInArgs: fn => [],
    typeChecker: isIsSimilar,
  }
}
